import type { BookLibrary } from "../../models/types";
import { findCover } from "../../services/openlibrary";
import * as storage from "../../services/storage";

let _overlayEl: HTMLDivElement | null = null;

function esc(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function showOverlay(html: string): void {
  if (!_overlayEl) {
    _overlayEl = document.createElement("div");
    _overlayEl.className = "saving-overlay";
    document.body.appendChild(_overlayEl);
  }
  _overlayEl.innerHTML = html;
}

function removeOverlay(): void {
  if (_overlayEl) {
    _overlayEl.remove();
    _overlayEl = null;
  }
}

function scanningHtml(
  processed: number,
  total: number,
  title: string,
): string {
  return `
    <div class="saving-overlay__panel">
      <div class="saving-overlay__indicator"></div>
      <div class="saving-overlay__content">
        <div class="saving-overlay__title">SCANNING COVERS</div>
        <div class="saving-overlay__subtitle">Accessing Open Library Database</div>
        <div class="saving-overlay__text" id="scan-status">
          Searching: ${esc(title)}<br/>
          ${processed} of ${total} books scanned
        </div>
        <div class="saving-overlay__actions">
          <button class="lcars-btn lcars-btn--red" id="scan-cancel-btn">Cancel</button>
        </div>
      </div>
      <div class="saving-overlay__indicator saving-overlay__indicator--alt"></div>
    </div>
  `;
}

function completeHtml(
  found: number,
  total: number,
  cancelled: boolean,
): string {
  const title = cancelled ? "SCAN CANCELLED" : "SCAN COMPLETE";
  const detail =
    found > 0
      ? `Located covers for ${found} of ${total} books`
      : `No new covers located for ${total} books`;

  return `
    <div class="saving-overlay__panel">
      <div class="saving-overlay__indicator"></div>
      <div class="saving-overlay__content">
        <div class="saving-overlay__title">${title}</div>
        <div class="saving-overlay__subtitle">Library Computer Access Protocol</div>
        <div class="saving-overlay__text">${detail}</div>
        <div class="saving-overlay__actions">
          <button class="lcars-btn lcars-btn--blue" id="scan-close-btn">Close</button>
        </div>
      </div>
      <div class="saving-overlay__indicator saving-overlay__indicator--alt"></div>
    </div>
  `;
}

/**
 * Scan the library for books missing cover images and try to find them
 * via the Open Library API. Shows a full-screen LCARS overlay with progress.
 *
 * @param library  The library to scan (books are mutated in-place when covers are found)
 * @param onComplete  Called after the overlay is dismissed so the caller can re-render
 */
export async function runCoverScan(
  library: BookLibrary,
  onComplete: () => void,
): Promise<void> {
  const books = library.books.filter((b) => !b.coverUrl);

  if (books.length === 0) {
    onComplete();
    return;
  }

  let _cancelled = false;
  showOverlay(scanningHtml(0, books.length, books[0].title));
  _overlayEl
    ?.querySelector("#scan-cancel-btn")
    ?.addEventListener("click", () => {
      _cancelled = true;
    });

  let found = 0;
  let unsaved = 0;

  for (let i = 0; i < books.length; i++) {
    const book = books[i];

    // Update progress display
    const statusEl = _overlayEl?.querySelector("#scan-status");
    if (statusEl) {
      statusEl.innerHTML = `Searching: ${esc(book.title)}<br/>${i} of ${books.length} books scanned`;
    }

    try {
      const coverUrl = await findCover(book.title, book.author, book.isbn);
      if (coverUrl) {
        book.coverUrl = coverUrl;
        found++;
        unsaved++;
      }
    } catch {
      // Skip this book on error
    }

    // Save every 10 books that got new covers
    if (unsaved >= 10) {
      try {
        await storage.save(library);
        unsaved = 0;
      } catch {
        // Save failed — covers are still in memory state
      }
    }

    // Check cancel after finishing the current book
    if (_cancelled) break;
  }

  // Final save for any remaining unsaved covers (including after cancel)
  if (unsaved > 0) {
    try {
      await storage.save(library);
    } catch {
      // Save failed — covers are still in memory state
    }
  }

  // Show completion summary and wait for user to dismiss
  showOverlay(completeHtml(found, books.length, _cancelled));

  await new Promise<void>((resolve) => {
    _overlayEl
      ?.querySelector("#scan-close-btn")
      ?.addEventListener("click", () => resolve());
  });

  removeOverlay();
  onComplete();
}
